import {FC, useState, useRef, useEffect, KeyboardEvent, ClipboardEvent} from "react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Typography,
  Avatar,
  TextField,
  Link,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {toast} from "react-toastify";
import {useVerifyOtpMutation, useSendOtpMutation} from "../../../app/services/otp";
import {getApiErrorMessage} from "../../../util/getApiErrorMessage";
import {useAppDispatch} from "../../../app/hooks";
import {setToken, toToken} from "../../../app/features/authSlice";
import { CustomerPortalConfig } from "../../../app/interfaces/dealerCustomer";

const isEmail = (value: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
};

export const OtpVerificationPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const sessionId = location.state?.sessionId || "";
  const identifier = location.state?.identifier || "";
  const maskedEmail = location.state?.maskedEmail;
  const maskedPhoneNumber = location.state?.maskedPhoneNumber;

  const [verifyOtp, {isLoading}] = useVerifyOtpMutation();
  const [sendOtp, {isLoading: isResending}] = useSendOtpMutation();

  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [hostOrigin, setHostOrigin] = useState<string | null>(null);
  
  const [config, setConfig] = useState<CustomerPortalConfig | null>(
    location.state?.config || null
  );

  const dealerWebsite = config?.dealerWebsite || location.state?.dealerWebsite;

  // Validate hostOrigin from URL parameters
  // TODO: Uncomment validation before production
  useEffect(() => {
    const hostOriginParam = searchParams.get("hostOrigin");
    // if (hostOriginParam && ALLOWED_PARENT_ORIGINS.includes(hostOriginParam)) {
    //   setHostOrigin(hostOriginParam);
    // } else if (hostOriginParam) {
    //   console.error("Untrusted hostOrigin:", hostOriginParam);
    // }
    
    if (hostOriginParam) {
      setHostOrigin(hostOriginParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (config) return;

    const handleMessage = (event: MessageEvent) => {
      // TODO: Uncomment origin validation before production
      // if (hostOrigin) {
      //   const allowedOrigin = new URL(hostOrigin).origin;
      //   if (event.origin !== allowedOrigin) {
      //     console.debug("Ignored message from unexpected origin:", event.origin);
      //     return;
      //   }
      // }

      if (event.data?.type === "CUSTOMER_PORTAL_CONFIG") {
        setConfig({
          productId: event.data.productId,
          dealerWebsite: event.data.dealerWebsite,
          clientId: event.data.clientId,
          clientSecret: event.data.clientSecret,
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [hostOrigin, config]);

  useEffect(() => {
    if (hostOrigin && !config) {
      window.parent.postMessage({type: "REQUEST_CUSTOMER_PORTAL_CONFIG"}, hostOrigin);
    }
  }, [hostOrigin, config]);

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    if (otp.every(d => d !== "") && !isLoading) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // Focus the first OTP input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const displayContact = maskedEmail || maskedPhoneNumber || identifier || "your registered contact";

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleVerify();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    
    if (!/^\d+$/.test(pastedData)) {
      toast.error("Please paste numbers only");
      return;
    }

    const pastedDigits = pastedData.slice(0, 6).split("");
    const newOtp = [...otp];
    
    pastedDigits.forEach((digit, index) => {
      if (index < 6) {
        newOtp[index] = digit;
      }
    });
    
    setOtp(newOtp);
    
    const nextIndex = Math.min(pastedDigits.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleVerify = async () => {
    const otpValue = otp.join("");

    if (otpValue.length !== 6) {
      toast.error("Please enter a complete 6-digit OTP");
      return;
    }

    if (isLoading) return;

    if (!currentSessionId) {
      toast.error("Session expired. Please request a new OTP.");
      navigate("/otp-request");
      return;
    }

    const clientId = config?.clientId || "";
    const clientSecret = config?.clientSecret || "";
    const productId = config?.productId || "";

    try {
      const response = await verifyOtp({
        sessionId: currentSessionId,
        otpCode: otpValue,
        clientId,
        clientSecret,
        productId,
      }).unwrap();

      dispatch(setToken(toToken(response)));

      toast.success("OTP verified successfully!");

      if (hostOrigin) {
        window.parent.postMessage(
          {
            type: "LOGIN_SUCCESS",
            access_token: response.access_token,
            refresh_token: response.refresh_token,
            expires_in: response.expires_in,
            token_type: response.token_type,
            scope: response.scope,
          },
          hostOrigin,
        );
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      toast.error(errorMessage || "Invalid OTP. Please try again.");
    }
  };

  const handleResend = async () => {
    if (!identifier) {
      toast.error("No identifier found. Please start over.");
      navigate("/otp-request");
      return;
    }

    try {
      const isEmailInput = isEmail(identifier);
      
      const response = await sendOtp({
        email: isEmailInput ? identifier : "",
        phoneNumber: isEmailInput ? undefined : identifier,
        dealerWebsite: dealerWebsite,
      }).unwrap();

      setCurrentSessionId(response.sessionId || "");
      
      toast.success(response.message || "OTP has been resent successfully!");
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (error: any) {
      const errorMessage = error?.data?.message || "Failed to resend OTP. Please try again.";
      toast.error(errorMessage);
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "background.paper",
          padding: 4,
          borderRadius: 2,
          boxShadow: 3,
          width: "100%",
        }}>
        <Avatar sx={{bgcolor: "primary.main", mb: 2}}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography component="h1" variant="h5" sx={{mb: 1}}>
          Enter Passcode
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{mb: 1}}>
          Enter the 6-digit code sent to
        </Typography>

        <Typography
          variant="body2"
          color="primary"
          fontWeight="medium"
          align="center"
          sx={{mb: 3}}>
          {displayContact}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 3,
            justifyContent: "center",
          }}>
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={el => (inputRefs.current[index] = el)}
              value={digit}
              onChange={e => handleChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                },
              }}
              sx={{
                width: "50px",
                "& input": {
                  padding: "12px",
                },
              }}
              variant="outlined"
            />
          ))}
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={handleVerify}
          sx={{mb: 2, py: 1.2}}
          disabled={isLoading}>
          {isLoading ? "Verifying..." : "Verify & Sign In"}
        </Button>

        <Box sx={{textAlign: "center"}}>
          <Typography variant="body2" color="text.secondary">
            Can't find your code? Check your spam/junk folder or search for our email.{" "}
            <Link
              component="button"
              variant="body2"
              onClick={handleResend}
              disabled={isResending}
              sx={{
                cursor: isResending ? "not-allowed" : "pointer",
                textDecoration: "underline",
              }}>
              {isResending ? "Resending..." : "Resend Code"}
            </Link>
          </Typography>
        </Box>

        <Box sx={{mt: 3, textAlign: "center"}}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate("/otp-request")}
            sx={{cursor: "pointer"}}>
            ← Try a different Email or Phone
          </Link>
        </Box>
      </Box>
    </Container>
  );
};
